import {
  OPERATING_KPI_COMPANY_VIEWS,
  OPERATING_KPI_ROUTE_VIEW_TO_RENDER_VIEW,
} from '../../../features/operating-kpis/operatingKpiCompanyViews';
import { INDUSTRY_METRICS_VIEWS as VIEWS } from '../../../configs/industryMetrics';
import {
  INDUSTRY_DATASET_IDS,
  normalizeIndustryTicker,
  persistIndustryDatasetState,
} from '../../../configs/industryDatasetState';

export const OPERATING_METRICS_DEPTHS = {
  CATEGORY: 'category',
  VENDOR: 'vendor',
  COMPANY: 'company',
};

export const OPERATING_CATEGORY_VIEWS = {
  TRENDS: 'trends',
  VALUATION: 'valuation',
  DISTRIBUTION: VIEWS.TRENDED,
};

export const OPERATING_GUIDANCE_VIEWS = {
  RESULTS: 'guidance-results',
  REVISIONS: 'guidance-revisions',
};

export const OPERATING_CURRENT_METRICS_VIEWS = [
  VIEWS.CHART,
  VIEWS.SCATTER,
  VIEWS.ACCELERATION,
  VIEWS.QUADRANT,
];

export const OPERATING_METRICS_VIEW_OPTIONS_BY_DEPTH = {
  [OPERATING_METRICS_DEPTHS.CATEGORY]: [
    OPERATING_CATEGORY_VIEWS.TRENDS,
    OPERATING_CATEGORY_VIEWS.VALUATION,
    OPERATING_CATEGORY_VIEWS.DISTRIBUTION,
    OPERATING_GUIDANCE_VIEWS.RESULTS,
    OPERATING_GUIDANCE_VIEWS.REVISIONS,
  ],
  [OPERATING_METRICS_DEPTHS.VENDOR]: [
    VIEWS.TABLE,
    VIEWS.CHART,
    VIEWS.SCATTER,
    VIEWS.ACCELERATION,
    VIEWS.QUADRANT,
    OPERATING_GUIDANCE_VIEWS.RESULTS,
    OPERATING_GUIDANCE_VIEWS.REVISIONS,
  ],
  [OPERATING_METRICS_DEPTHS.COMPANY]: [
    OPERATING_KPI_COMPANY_VIEWS.DASHBOARD,
    OPERATING_KPI_COMPANY_VIEWS.TRAJECTORY,
    OPERATING_KPI_COMPANY_VIEWS.MARGINS,
    OPERATING_GUIDANCE_VIEWS.RESULTS,
    OPERATING_GUIDANCE_VIEWS.REVISIONS,
  ],
};

export const DEFAULT_OPERATING_METRICS_NAV = {
  depth: OPERATING_METRICS_DEPTHS.CATEGORY,
  view: OPERATING_CATEGORY_VIEWS.TRENDS,
};

export const isValidOperatingMetricsDepth = (depth) => (
  Object.values(OPERATING_METRICS_DEPTHS).includes(depth)
);

export const isValidOperatingMetricsViewForDepth = (depth, view) => (
  Boolean(OPERATING_METRICS_VIEW_OPTIONS_BY_DEPTH[depth]?.includes(view))
);

export const normalizeOperatingMetricsNav = ({
  depth,
  view,
} = {}) => {
  const normalizedDepth = isValidOperatingMetricsDepth(depth)
    ? depth
    : DEFAULT_OPERATING_METRICS_NAV.depth;
  const normalizedView = isValidOperatingMetricsViewForDepth(normalizedDepth, view)
    ? view
    : OPERATING_METRICS_VIEW_OPTIONS_BY_DEPTH[normalizedDepth]?.[0] || DEFAULT_OPERATING_METRICS_NAV.view;

  return {
    depth: normalizedDepth,
    view: normalizedView,
  };
};

export const buildOperatingMetricsPath = ({
  sectionId,
  depth,
  viewId,
  view,
} = {}) => {
  const nav = normalizeOperatingMetricsNav({
    depth: depth || sectionId,
    view: view || viewId,
  });

  return `/kpis/${nav.depth}/${nav.view}`;
};

export const buildOperatingMetricsCompanyPath = ({
  viewId = OPERATING_KPI_COMPANY_VIEWS.DASHBOARD,
  view = viewId,
} = {}) => buildOperatingMetricsPath({
  sectionId: OPERATING_METRICS_DEPTHS.COMPANY,
  viewId: view,
});

export const persistOperatingMetricsCompanySelection = ({
  ticker,
  viewId = OPERATING_KPI_COMPANY_VIEWS.DASHBOARD,
} = {}) => {
  const normalizedTicker = normalizeIndustryTicker(ticker);
  if (!normalizedTicker) return '';

  persistIndustryDatasetState({
    datasetId: INDUSTRY_DATASET_IDS.OPERATING_KPIS,
    layerId: OPERATING_METRICS_DEPTHS.COMPANY,
    viewId,
    filters: {
      ticker: normalizedTicker,
    },
  });

  return normalizedTicker;
};

export const buildLegacyOperatingMetricsRedirectPath = ({ view } = {}) => {
  if (!view) return buildOperatingMetricsPath();

  const legacyView = view;
  const depth = legacyView === VIEWS.TRENDED
    ? OPERATING_METRICS_DEPTHS.CATEGORY
    : OPERATING_METRICS_DEPTHS.VENDOR;

  return buildOperatingMetricsPath({ depth, view: legacyView });
};

export const getOperatingMetricsCompanyRouteView = (viewId) => (
  OPERATING_KPI_ROUTE_VIEW_TO_RENDER_VIEW[viewId]
    ? viewId
    : OPERATING_KPI_COMPANY_VIEWS.DASHBOARD
);

export const isOperatingGuidanceView = (viewId) => (
  Object.values(OPERATING_GUIDANCE_VIEWS).includes(viewId)
);

export const isOperatingCurrentMetricsView = (viewId) => (
  OPERATING_CURRENT_METRICS_VIEWS.includes(viewId)
);

export const getOperatingMetricsSidebarViewId = (depth, viewId) => {
  if (isOperatingGuidanceView(viewId)) return `${depth}-${OPERATING_GUIDANCE_VIEWS.RESULTS}`;
  if (depth === OPERATING_METRICS_DEPTHS.VENDOR && isOperatingCurrentMetricsView(viewId)) {
    return `${depth}-${VIEWS.CHART}`;
  }
  return `${depth}-${viewId}`;
};

export const getGuidanceViewFromOperatingView = (viewId) => (
  viewId === OPERATING_GUIDANCE_VIEWS.REVISIONS ? 'revisions' : 'results'
);

export const getOperatingViewFromGuidanceView = (viewId) => (
  viewId === 'revisions' ? OPERATING_GUIDANCE_VIEWS.REVISIONS : OPERATING_GUIDANCE_VIEWS.RESULTS
);
